import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { environment } from '../environments/environmentCAS';

export interface AlptisTarificationRequest {
  produit?: string;
  date_effet: string;
  assures: {
    adherent: {
      cadre_exercice: string;
      categorie_socioprofessionnelle: string;
      code_postal: string;
      date_naissance: string;
      micro_entrepreneur: boolean;
      regime_obligatoire: string;
      statut_professionnel: string;
    };
    conjoint?: {
      categorie_socioprofessionnelle: string;
      date_naissance: string;
      regime_obligatoire: string;
    };
    enfants?: Array<{
      date_naissance: string;
      regime_obligatoire: string;
    }>;
  };
  combinaisons: Array<{
    numero: number;
    offre: {
      niveau: string;
      sur_complementaire: boolean;
    };
    ayants_droit: {
      conjoint?: boolean;
      enfants?: number;
    };
    commissionnement: string;
  }>;
}

export interface AlptisTarificationResponse {
  resultatsTarification: Array<{
    tarifs: {
      cotisationMensuelle: number;
      cotisationAnnuelle: number;
      fraisGestion: number;
      taxeAttentat: number;
      tarifTTC: number;
    };
    generationTarif: string;
    numeroCombinaison: number;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class AlptisService {
  private readonly baseUrl = `${environment.casApiUrl}${environment.apiEndpoint}/v1/tarification`;

  constructor(private http: HttpClient) {}

  getTarification(request: AlptisTarificationRequest): Observable<AlptisTarificationResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<AlptisTarificationResponse>(`${this.baseUrl}/alptis`, request, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de la tarification Alptis:', error);
        throw error;
      })
    );
  }

  getOffres(): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    return this.http.get<any>(`${this.baseUrl}/alptis/offres`, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de la récupération des offres Alptis:', error);
        throw error;
      })
    );
  }

  generateDevis(request: any, testMode: boolean = false): Observable<Blob> {
    const endpoint = testMode
      ? environment.endpoints.alptis.devisTest
      : environment.endpoints.alptis.devis;
    const url = `${environment.casApiUrl}${endpoint}`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post(url, request, { headers, responseType: 'blob' }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de la génération du devis Alptis:', error);
        throw error;
      })
    );
  }

  downloadEtudePersonnalisee(projetId: string | number): Observable<Blob> {
    const base = environment.endpoints.alptis.etudePersonnaliseeBase;
    const url = `${environment.casApiUrl}${base}/${projetId}/etude-personnalisee`;

    const headers = new HttpHeaders({
      'Accept': 'application/pdf'
    });

    return this.http.get(url, { headers, responseType: 'blob' }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors du téléchargement de l\'étude personnalisée Alptis:', error);
        throw error;
      })
    );
  }

  generateEtudePersonnalisee(payload: any): Observable<Blob> {
    const endpoint = environment.endpoints.alptis.etudePersonnaliseeGenerate;
    const url = `${environment.casApiUrl}${endpoint}`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return new Observable<Blob>((observer) => {
      this.http.post(url, payload, { headers, responseType: 'blob', observe: 'response' }).subscribe({
        next: (response) => {
          const contentType = response.headers.get('content-type') || '';
          const body = response.body as Blob;
          
          // Si c'est un PDF, retourner directement
          if (contentType.includes('application/pdf')) {
            observer.next(body);
            observer.complete();
            return;
          }
          
          // Sinon, essayer de parser comme JSON
          body.text().then((text) => {
            try {
              const parsed = JSON.parse(text);
              if (parsed.success === false) {
                // Retourner une erreur avec le message du backend
                observer.error({
                  message: parsed.message || parsed.detail || 'Erreur lors de la génération de l\'étude personnalisée',
                  error: parsed
                });
              } else {
                // Si c'est un autre JSON, retourner le texte comme erreur
                observer.error({
                  message: parsed.message || parsed.detail || text,
                  error: parsed
                });
              }
            } catch {
              // Pas du JSON, retourner comme blob
              observer.next(new Blob([text], { type: contentType }));
              observer.complete();
            }
          }).catch((err) => {
            observer.error({ message: 'Erreur lors de la lecture de la réponse', error: err });
          });
        },
        error: (error) => {
          // Gérer les erreurs HTTP avec Blob
          if (error?.error instanceof Blob) {
            error.error.text().then((text: string) => {
              try {
                const parsed = JSON.parse(text);
                observer.error({ ...error, error: parsed, message: parsed?.message || error?.message });
              } catch {
                observer.error({ ...error, message: text || error?.message });
              }
            }).catch(() => {
              observer.error(error);
            });
          } else {
            observer.error(error);
          }
        }
      });
    });
  }

  // Nouvelle méthode pour l'endpoint sante-frontaliers-suisses
  getFrontaliersTarification(request: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const url = `${environment.casApiUrl}${environment.apiEndpoint}/v1/tarification/alptis/frontaliers`;
    
    return this.http.post<any>(url, request, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de la tarification Alptis Frontaliers Suisses:', error);
        throw error;
      })
    );
  }

  /**
   * Génère l'étude personnalisée pour un projet Santé Protect
   * Endpoint: GET /api/creer-etude-personnalisee/{id}?code_distributeur=...
   */
  generateSanteProtectEtudePersonnalisee(projetId: string | number, codeDistributeur?: string): Observable<Blob> {
    let url = `${environment.casApiUrl}/api/creer-etude-personnalisee/${projetId}`;
    if (codeDistributeur) {
      url += `?code_distributeur=${encodeURIComponent(codeDistributeur)}`;
    }

    const headers = new HttpHeaders({
      'Accept': 'application/pdf'
    });

    return this.http.get(url, { headers, responseType: 'blob' }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de la génération de l\'étude personnalisée Santé Protect:', error);
        throw error;
      })
    );
  }

  /**
   * Génère la demande d'adhésion pour un projet Santé Protect
   */
  generateSanteProtectDemandeAdhesion(projetId: string | number, codeDistributeur?: string): Observable<Blob> {
    let url = `${environment.casApiUrl}${environment.apiEndpoint}/v1/alptis/sante-protect/demande-adhesion/${projetId}`;
    if (codeDistributeur) {
      url += `?codeDistributeur=${encodeURIComponent(codeDistributeur)}`;
    }

    const headers = new HttpHeaders({
      'Accept': 'application/pdf'
    });

    return this.http.get(url, { headers, responseType: 'blob' }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de la génération de la demande d\'adhésion Santé Protect:', error);
        throw error;
      })
    );
  }

  /**
   * Récupère les documents statiques pour Santé Protect
   */
  getSanteProtectDocumentsStatiques(types: string[]): Observable<Blob> {
    const typesParam = types.join(',');
    const url = `${environment.casApiUrl}${environment.apiEndpoint}/v1/alptis/sante-protect/documents-statiques?types=${encodeURIComponent(typesParam)}`;

    const headers = new HttpHeaders({
      'Accept': 'application/pdf'
    });

    return this.http.get(url, { headers, responseType: 'blob' }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de la récupération des documents statiques Santé Protect:', error);
        throw error;
      })
    );
  }
}
